import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../lib/session.tsx";

const STEPS = [
  {
    route: "/",
    eyebrow: "01 / COMMAND",
    title: "See the whole campaign at a glance",
    body: "The command center turns evidence, deadlines, field work, compliance, and public narrative into one prioritized operating picture.",
    cue: "Notice the election clock, open reviews, and narrative alerts.",
  },
  {
    route: "/evidence",
    eyebrow: "02 / GROUND TRUTH",
    title: "Evidence enters before AI opinions leave",
    body: "Every source is classified, extracted, reviewed, and assigned a reliability tier. Only approved excerpts can support factual AI output.",
    cue: "Open a source to inspect its excerpt, tier, and proposed claims.",
  },
  {
    route: "/advisor",
    eyebrow: "03 / STRATEGY",
    title: "The advisor shows its work",
    body: "AURA separates facts, inferences, and recommendations; cites the supporting record; and quarantines claims it cannot verify.",
    cue: "The demo includes completed answers without spending API credits.",
  },
  {
    route: "/watchdog",
    eyebrow: "04 / SIGNAL",
    title: "Watch stories climb before they break",
    body: "Watchdog monitors public coverage while the Anecdotal Meter distinguishes social chatter, blogs, news, and official records.",
    cue: "Look for momentum, breakouts, and candidate-conduct alerts.",
  },
  {
    route: "/field",
    eyebrow: "05 / EXECUTION",
    title: "Move from strategy to doors",
    body: "Field operations, contacts, assignments, events, and the team board keep the campaign synchronized around real work.",
    cue: "Demo people and locations are fictional and intentionally isolated.",
  },
  {
    route: "/finance",
    eyebrow: "06 / CONTROL",
    title: "Compliance is part of the workflow",
    body: "Expenses, related-party checks, receipts, filings, and approval gates create an auditable path instead of a spreadsheet scramble.",
    cue: "Review the mock flags and filing calendar—nothing here is real campaign data.",
  },
  {
    route: "/report",
    eyebrow: "07 / DECISION",
    title: "Finish with the campaign brief",
    body: "Reports combine objectives, deadlines, unresolved risks, compliance status, and execution progress into a decision-ready briefing.",
    cue: "Use the navigation freely; this tenant is permanently read-only.",
  },
] as const;

export const DEMO_TOUR_SESSION_KEY = "canales-demo-tour-seen";

export function DemoExperience() {
  const { demoMode, locale } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex]!;

  useEffect(() => {
    if (!demoMode) return;
    if (sessionStorage.getItem(DEMO_TOUR_SESSION_KEY) === "true") return;
    sessionStorage.setItem(DEMO_TOUR_SESSION_KEY, "true");
    setStepIndex(0);
    setOpen(true);
  }, [demoMode]);

  useEffect(() => {
    if (!open || !demoMode) return;
    navigate(step.route);
  }, [demoMode, navigate, open, step.route]);

  if (!demoMode) return null;

  const spanish = locale === "es";
  return (
    <>
      <section className="demo-ribbon" aria-label="Read-only demonstration">
        <div className="demo-ribbon-mark">DEMO / 2026</div>
        <div>
          <strong>
            {spanish ? "Entorno de demostración aislado" : "Isolated demonstration environment"}
          </strong>
          <span>
            {spanish
              ? " Datos ficticios · solo lectura · sin acceso a la campaña real"
              : " Fictional data · read-only · no access to the live campaign"}
          </span>
        </div>
        <button
          type="button"
          className="demo-tour-launch"
          onClick={() => {
            setStepIndex(0);
            setOpen(true);
          }}
        >
          {spanish ? "Iniciar recorrido" : "Start guided tour"} →
        </button>
      </section>

      {open && (
        <div
          className="demo-tour-veil"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-tour-title"
        >
          <aside className="demo-tour-panel">
            <div className="demo-tour-topline">
              <span>CANALES CAMPAIGNOS</span>
              <button type="button" aria-label="Close tour" onClick={() => setOpen(false)}>
                ×
              </button>
            </div>
            <div
              className="demo-tour-progress"
              aria-label={`Step ${stepIndex + 1} of ${STEPS.length}`}
            >
              {STEPS.map((item, index) => (
                <button
                  key={item.eyebrow}
                  type="button"
                  className={index === stepIndex ? "active" : ""}
                  aria-label={`Go to step ${index + 1}`}
                  onClick={() => setStepIndex(index)}
                />
              ))}
            </div>
            <div className="demo-tour-copy">
              <div className="demo-tour-eyebrow">{step.eyebrow}</div>
              <h2 id="demo-tour-title">{step.title}</h2>
              <p>{step.body}</p>
              <div className="demo-tour-cue">
                <b>LOOK FOR</b>
                {step.cue}
              </div>
            </div>
            <div className="demo-tour-actions">
              <button type="button" className="subtle" onClick={() => setOpen(false)}>
                Explore freely
              </button>
              <div>
                {stepIndex > 0 && (
                  <button type="button" onClick={() => setStepIndex(stepIndex - 1)}>
                    Back
                  </button>
                )}
                {stepIndex < STEPS.length - 1 ? (
                  <button
                    type="button"
                    className="primary"
                    onClick={() => setStepIndex(stepIndex + 1)}
                  >
                    Next briefing
                  </button>
                ) : (
                  <button type="button" className="primary" onClick={() => setOpen(false)}>
                    Enter the demo
                  </button>
                )}
              </div>
            </div>
            <div className="demo-tour-foot">
              READ-ONLY SANDBOX · {stepIndex + 1}/{STEPS.length}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
